// repo/primary-care-connect/frontend/src/pages/Portal/FamilyDoctor/Profile/ProfilePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import "./ProfilePage.css";

import {
  ApiError,
  FamilyDoctorProfileResponse,
  updateFamilyDoctorIntake,
  updateFamilyDoctorServices,
  updateFamilyDoctorCapacity,
  getFamilyDoctorProfile,
} from "../../../../api/portalFamilyDoctor";

import IntakeRulesSection from "./components/IntakeRulesSection";

type IdName = { id: number; name: string };

function formatDateTime(val: string | null | undefined) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function dayName(n: number) {
  const map: Record<number, string> = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
    7: "Sunday",
  };
  return map[n] || `Day ${n}`;
}

function compactTime(t: string | null | undefined) {
  if (!t) return "—";

  const s = String(t).trim();
  if (!s) return "—";

  // Accept "HH:MM:SS" or "HH:MM:SS.fffffff" or "HH:MM"
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "—";

  const hh = String(m[1]).padStart(2, "0");
  const mm = String(m[2]).padStart(2, "0");
  return `${hh}:${mm}`;
}

function pickFirstNumber(...vals: any[]): number | null {
  for (const v of vals) {
    const n = parseInt(String(v), 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

function toExternalUrl(raw: string | null | undefined): string | null {
  const s = String(raw || "").trim();
  if (!s) return null;

  // Already absolute
  if (/^https?:\/\//i.test(s)) return s;

  // Protocol-relative (rare, but valid)
  if (/^\/\//.test(s)) return `https:${s}`;

  // Otherwise treat as domain/path and force https
  return `https://${s}`;
}

async function getLookup(url: string): Promise<IdName[]> {
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "same-origin",
  });

  const text = await res.text().catch(() => "");
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    throw new Error(
      (data && (data.error || data.message)) || `Lookup failed (${res.status})`
    );
  }

  if (!Array.isArray(data)) return [];

  const rows: IdName[] = data
    .map((x: any) => {
      const id = pickFirstNumber(
        x?.id,
        x?.Id,
        x?.LanguageID,
        x?.VisitTypeID,
        x?.CoverageTypeID
      );
      const name = String(x?.name ?? x?.Name ?? "").trim();
      return id && name ? { id, name } : null;
    })
    .filter(Boolean) as IdName[];

  rows.sort((a, b) => a.name.localeCompare(b.name));
  return rows;
}

function getSelectedIdsFromMultiSelect(
  e: React.ChangeEvent<HTMLSelectElement>
): number[] {
  const out: number[] = [];
  const opts = Array.from(e.target.selectedOptions || []);
  for (const o of opts) {
    const n = parseInt(String(o.value), 10);
    if (Number.isFinite(n) && n > 0) out.push(n);
  }
  return out;
}

function safeString(v: any): string | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

function tryFormatDate(val: any): string | null {
  const s = safeString(val);
  if (!s) return null;

  // If it's already YYYY-MM-DD, keep it.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;

  return d.toISOString().slice(0, 10);
}

function numberToInputValue(v: number | null | undefined): string {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return String(Math.trunc(n));
}

function parseNullableWholeNumber(raw: string): number | null {
  const s = String(raw || "").trim();
  if (!s) return null;

  const n = Number(s);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
    throw new Error("Capacity values must be blank or a whole number of 0 or more.");
  }

  return n;
}

/**
 * ✅ ClinicSettings "special hours" normalizer
 * Supports BOTH:
 * - legacy keys (specialHours / clinicSpecialHours)
 * - v1 locked structure coming from ClinicAdmin settings:
 *   - hours.closures              (array)
 *   - hours.temporaryOverride     (object)
 *
 * Output format (renderSpecialHourRow supports this):
 * - { date, isClosed, reason } for closures
 * - { startDate, endDate, hoursByDay, notePublic } for overrides (rendered in a readable way below)
 */
function normalizeSpecialHoursFromClinicSettings(clinic: any): any[] {
  const cs = clinic?.clinicSettings;
  if (!cs || typeof cs !== "object") return [];

  // 1) Legacy / older shapes (keep)
  const legacyCandidates = [
    cs?.specialHours,
    cs?.hours?.specialHours,
    cs?.clinicSpecialHours,
    cs?.hours?.clinicSpecialHours,
  ];
  const legacyArr = legacyCandidates.find((x) => Array.isArray(x));
  if (Array.isArray(legacyArr) && legacyArr.length) return legacyArr.filter(Boolean);

  // 2) v1 Clinic Admin shape: closures + temporaryOverride
  const hours = cs?.hours && typeof cs.hours === "object" ? cs.hours : null;
  if (!hours) return [];

  const out: any[] = [];

  // closures: [{date, reason}]
  if (Array.isArray(hours.closures)) {
    for (const c of hours.closures) {
      const date = tryFormatDate(c?.date) || tryFormatDate(c?.Date);
      if (!date) continue;

      out.push({
        kind: "closure",
        date,
        isClosed: true,
        label: safeString(c?.reason) || safeString(c?.Reason) || "Closed",
      });
    }
  }

  // temporaryOverride: { start, end, notePublic, hoursByDay }
  // We render this as a single "range" row, and include a readable summary string.
  const to = hours.temporaryOverride;
  if (to && typeof to === "object") {
    const startDate =
      tryFormatDate((to as any)?.start) || tryFormatDate((to as any)?.Start);
    const endDate =
      tryFormatDate((to as any)?.end) || tryFormatDate((to as any)?.End);
    const notePublic =
      safeString((to as any)?.notePublic) || safeString((to as any)?.NotePublic);

    // Build a compact human readable summary for hoursByDay (only days provided)
    const hbd = (to as any)?.hoursByDay;
    let summary: string | null = null;

    if (hbd && typeof hbd === "object") {
      const parts: string[] = [];
      const keys = Object.keys(hbd)
        .filter((k) => /^[0-6]$/.test(String(k)))
        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

      for (const k of keys) {
        const v = (hbd as any)[k];
        if (!v || typeof v !== "object") continue;

        const isClosed = v?.isClosed === true;
        const open = safeString(v?.open);
        const close = safeString(v?.close);

        const dayLabel = dayName(parseInt(k, 10));
        if (isClosed) {
          parts.push(`${dayLabel}: Closed`);
        } else if (open && close) {
          parts.push(`${dayLabel}: ${open}–${close}`);
        }
      }

      if (parts.length) summary = parts.join(" • ");
    }

    // Only include if we have at least a range or note/summary
    if (startDate || endDate || notePublic || summary) {
      out.push({
        kind: "temporaryOverride",
        startDate: startDate || null,
        endDate: endDate || null,
        label: notePublic || null,
        summary: summary || null,
      });
    }
  }

  return out;
}

/**
 * ✅ Clinic Admin-style rendering helpers (read-only)
 * Uses clinic.clinicSettings.hours: { closures[], temporaryOverride{}, walkIn{} }
 */
type ClinicHoursDisplay = {
  closures: { date: string; reason: string | null }[];
  temporaryOverride: null | {
    start: string | null;
    end: string | null;
    notePublic: string | null;
    hoursByDay: any;
  };
  walkIn: null | {
    enabled: boolean;
    notePublic: string | null;
    hoursByDay: any;
  };
};

function getClinicHoursDisplay(clinic: any): ClinicHoursDisplay {
  const cs = clinic?.clinicSettings;
  const hours = cs?.hours && typeof cs.hours === "object" ? cs.hours : null;

  const closures: { date: string; reason: string | null }[] = [];
  if (hours && Array.isArray((hours as any).closures)) {
    for (const c of (hours as any).closures) {
      const date = tryFormatDate(c?.date) || tryFormatDate(c?.Date);
      if (!date) continue;
      const reason = safeString(c?.reason) || safeString(c?.Reason) || null;
      closures.push({ date, reason });
    }
    closures.sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }

  const toRaw = hours && (hours as any).temporaryOverride;
  const temporaryOverride =
    toRaw && typeof toRaw === "object"
      ? {
          start: tryFormatDate(toRaw?.start) || tryFormatDate(toRaw?.Start) || null,
          end: tryFormatDate(toRaw?.end) || tryFormatDate(toRaw?.End) || null,
          notePublic: safeString(toRaw?.notePublic) || safeString(toRaw?.NotePublic) || null,
          hoursByDay: toRaw?.hoursByDay || null,
        }
      : null;

  const wiRaw = hours && (hours as any).walkIn;
  const walkIn =
    wiRaw && typeof wiRaw === "object"
      ? {
          enabled: wiRaw?.enabled === true,
          notePublic: safeString(wiRaw?.notePublic) || safeString(wiRaw?.NotePublic) || null,
          hoursByDay: wiRaw?.hoursByDay || null,
        }
      : null;

  return { closures, temporaryOverride, walkIn };
}

function renderHoursByDayForDisplay(hoursByDay: any) {
  if (!hoursByDay || typeof hoursByDay !== "object") return null;

  const keys = Object.keys(hoursByDay)
    .filter((k) => /^[0-6]$/.test(String(k)))
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));

  if (!keys.length) return null;

  return (
    <div className="fd-profile__kvlist">
      {keys.map((k) => {
        const v = hoursByDay[k];
        const isClosed = v?.isClosed === true;
        const open = !isClosed ? safeString(v?.open) : null;
        const close = !isClosed ? safeString(v?.close) : null;

        return (
          <div key={k} className="fd-profile__kvrow">
            <div className="fd-profile__kvkey">{dayName(parseInt(k, 10))}</div>
            <div className="fd-profile__kvval">
              {isClosed ? "Closed" : open && close ? `${open} – ${close}` : "—"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function renderSpecialHourRow(item: any, idx: number) {
  const date = tryFormatDate(item?.date) || tryFormatDate(item?.Date);
  const startDate =
    tryFormatDate(item?.startDate) ||
    tryFormatDate(item?.StartDate) ||
    tryFormatDate(item?.from) ||
    tryFormatDate(item?.From);
  const endDate =
    tryFormatDate(item?.endDate) ||
    tryFormatDate(item?.EndDate) ||
    tryFormatDate(item?.to) ||
    tryFormatDate(item?.To);

  const label =
    safeString(item?.label) ||
    safeString(item?.reason) ||
    safeString(item?.name) ||
    safeString(item?.Label) ||
    safeString(item?.Reason) ||
    null;

  const isClosed =
    item?.isClosed === true ||
    item?.IsClosed === true ||
    item?.closed === true ||
    item?.Closed === true;

  const openTime =
    safeString(item?.openTime) ||
    safeString(item?.OpenTime) ||
    safeString(item?.open) ||
    safeString(item?.Open) ||
    null;

  const closeTime =
    safeString(item?.closeTime) ||
    safeString(item?.CloseTime) ||
    safeString(item?.close) ||
    safeString(item?.Close) ||
    null;

  const when = date
    ? date
    : startDate && endDate
    ? `${startDate} – ${endDate}`
    : startDate
    ? startDate
    : endDate
    ? endDate
    : "—";

  // ✅ v1 temp override summary support
  const summary = safeString(item?.summary);

  const hoursText = isClosed
    ? "Closed"
    : summary
    ? summary
    : `${compactTime(openTime)} – ${compactTime(closeTime)}`;

  return (
    <div key={idx} className="fd-profile__kvrow">
      <div className="fd-profile__kvkey fd-profile__kvkey--wide">{when}</div>
      <div className="fd-profile__kvval">{hoursText}</div>
      {label ? <div style={{ color: "#6b7a94" }}>({label})</div> : null}
    </div>
  );
}

export default function ProfilePage() {
  const [data, setData] = useState<FamilyDoctorProfileResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState<boolean>(false);

  // Save states
  const [savingIntake, setSavingIntake] = useState(false);
  const [savingCapacity, setSavingCapacity] = useState(false);
  const [savingServices, setSavingServices] = useState(false);

  const [intakeError, setIntakeError] = useState<string | null>(null);
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [servicesError, setServicesError] = useState<string | null>(null);

  const [intakeSavedAt, setIntakeSavedAt] = useState<string | null>(null);
  const [capacitySavedAt, setCapacitySavedAt] = useState<string | null>(null);
  const [servicesSavedAt, setServicesSavedAt] = useState<string | null>(null);

  // Draft intake fields
  const [draftAccepting, setDraftAccepting] = useState<boolean>(false);
  const [draftIntakeNotes, setDraftIntakeNotes] = useState<string>("");
  const [draftBookingUrl, setDraftBookingUrl] = useState<string>("");

  // Draft capacity fields
  const [draftDeclaredCapacity, setDraftDeclaredCapacity] = useState<string>("");
  const [draftRemainingCapacityOverride, setDraftRemainingCapacityOverride] =
    useState<string>("");

  // Draft services fields (IDs)
  const [draftLanguageIds, setDraftLanguageIds] = useState<number[]>([]);
  const [draftVisitTypeIds, setDraftVisitTypeIds] = useState<number[]>([]);
  const [draftCoverageTypeIds, setDraftCoverageTypeIds] = useState<number[]>([]);

  // Lookup options for multi-select
  const [languageOptions, setLanguageOptions] = useState<IdName[]>([]);
  const [visitTypeOptions, setVisitTypeOptions] = useState<IdName[]>([]);
  const [coverageOptions, setCoverageOptions] = useState<IdName[]>([]);

  async function loadProfile() {
    const res = await getFamilyDoctorProfile();
    setData(res);

    // Intake drafts
    setDraftAccepting(!!res.doctor.acceptingNewPatients);
    setDraftIntakeNotes(res.doctor.intakeNotes || "");
    setDraftBookingUrl(res.doctor.firstAppointmentBookingUrl || "");

    // Capacity drafts
    setDraftDeclaredCapacity(numberToInputValue(res.doctor.declaredCapacity));
    setDraftRemainingCapacityOverride(
      numberToInputValue(res.doctor.remainingCapacityOverride)
    );

    // Services drafts
    const languages = res.services?.languages || [];
    const visitTypes = res.services?.visitTypes || [];
    const coverages = res.services?.coverages || [];

    setDraftLanguageIds(languages.map((x) => x.id));
    setDraftVisitTypeIds(visitTypes.map((x) => x.id));
    setDraftCoverageTypeIds(coverages.map((x) => x.id));

    return { languages, visitTypes, coverages };
  }

  async function loadLookupsWithFallback(fallback: {
    languages: IdName[];
    visitTypes: IdName[];
    coverages: IdName[];
  }) {
    try {
      const langs = await getLookup("/api/languages");
      setLanguageOptions(langs.length ? langs : fallback.languages);
    } catch {
      setLanguageOptions(fallback.languages);
    }

    try {
      const vts = await getLookup("/api/visit-types");
      setVisitTypeOptions(vts.length ? vts : fallback.visitTypes);
    } catch {
      setVisitTypeOptions(fallback.visitTypes);
    }

    try {
      const covs = await getLookup("/api/coverage-types");
      setCoverageOptions(covs.length ? covs : fallback.coverages);
    } catch {
      setCoverageOptions(fallback.coverages);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const fallback = await loadProfile();
        if (!mounted) return;

        await loadLookupsWithFallback(fallback);
      } catch (e: any) {
        if (!mounted) return;
        if (e instanceof ApiError) {
          setError(e.body?.error || e.message);
        } else {
          setError("Failed to load profile");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const identityCpso = useMemo(() => {
    if (!data) return "—";
    return data.doctor.cpso || data.doctor.cpsoNumber || "—";
  }, [data]);

  async function onSaveIntake() {
    if (!data) return;

    setSavingIntake(true);
    setIntakeError(null);
    setIntakeSavedAt(null);

    try {
      const payload = {
        acceptingNewPatients: !!draftAccepting,
        intakeNotes: draftIntakeNotes?.trim() ? draftIntakeNotes.trim() : null,
        firstAppointmentBookingUrl: draftBookingUrl?.trim()
          ? draftBookingUrl.trim()
          : null,
      };

      const res = await updateFamilyDoctorIntake(payload);

      setData({
        ...data,
        doctor: {
          ...data.doctor,
          ...res.doctor,
        },
      });

      setIntakeSavedAt(new Date().toISOString());
    } catch (e: any) {
      if (e instanceof ApiError) {
        setIntakeError(e.body?.error || e.message);
      } else {
        setIntakeError("Failed to save intake settings");
      }
    } finally {
      setSavingIntake(false);
    }
  }

  async function onSaveCapacity() {
    if (!data) return;

    setSavingCapacity(true);
    setCapacityError(null);
    setCapacitySavedAt(null);

    try {
      const declaredCapacity = parseNullableWholeNumber(draftDeclaredCapacity);
      const remainingCapacityOverride = parseNullableWholeNumber(
        draftRemainingCapacityOverride
      );

      if (
        declaredCapacity !== null &&
        remainingCapacityOverride !== null &&
        remainingCapacityOverride > declaredCapacity
      ) {
        throw new Error(
          "Remaining capacity override cannot be greater than declared capacity."
        );
      }

      const res = await updateFamilyDoctorCapacity({
        declaredCapacity,
        remainingCapacityOverride,
      });

      setData({
        ...data,
        doctor: {
          ...data.doctor,
          declaredCapacity: res.doctor.declaredCapacity,
          remainingCapacityOverride: res.doctor.remainingCapacityOverride,
          remainingCapacityUpdatedAt: res.doctor.remainingCapacityUpdatedAt,
        },
      });

      setDraftDeclaredCapacity(numberToInputValue(res.doctor.declaredCapacity));
      setDraftRemainingCapacityOverride(
        numberToInputValue(res.doctor.remainingCapacityOverride)
      );

      setCapacitySavedAt(new Date().toISOString());
    } catch (e: any) {
      if (e instanceof ApiError) {
        setCapacityError(e.body?.error || e.message);
      } else {
        setCapacityError(e?.message || "Failed to save capacity settings");
      }
    } finally {
      setSavingCapacity(false);
    }
  }

  async function onSaveServices() {
    if (!data) return;

    setSavingServices(true);
    setServicesError(null);
    setServicesSavedAt(null);

    try {
      const payload = {
        languageIds: draftLanguageIds,
        visitTypeIds: draftVisitTypeIds,
        coverageTypeIds: draftCoverageTypeIds,
      };

      const res = await updateFamilyDoctorServices(payload);

      setData({
        ...data,
        services: res.services,
      });

      setDraftLanguageIds(res.services.languages.map((x) => x.id));
      setDraftVisitTypeIds(res.services.visitTypes.map((x) => x.id));
      setDraftCoverageTypeIds(res.services.coverages.map((x) => x.id));

      setServicesSavedAt(new Date().toISOString());
    } catch (e: any) {
      if (e instanceof ApiError) {
        setServicesError(e.body?.error || e.message);
      } else {
        setServicesError("Failed to save services");
      }
    } finally {
      setSavingServices(false);
    }
  }

  function onCancelEdits() {
    if (!data) return;

    setEditMode(false);

    setDraftAccepting(!!data.doctor.acceptingNewPatients);
    setDraftIntakeNotes(data.doctor.intakeNotes || "");
    setDraftBookingUrl(data.doctor.firstAppointmentBookingUrl || "");

    setDraftDeclaredCapacity(numberToInputValue(data.doctor.declaredCapacity));
    setDraftRemainingCapacityOverride(
      numberToInputValue(data.doctor.remainingCapacityOverride)
    );

    const languages = data.services?.languages || [];
    const visitTypes = data.services?.visitTypes || [];
    const coverages = data.services?.coverages || [];

    setDraftLanguageIds(languages.map((x) => x.id));
    setDraftVisitTypeIds(visitTypes.map((x) => x.id));
    setDraftCoverageTypeIds(coverages.map((x) => x.id));

    setIntakeError(null);
    setCapacityError(null);
    setServicesError(null);
    setIntakeSavedAt(null);
    setCapacitySavedAt(null);
    setServicesSavedAt(null);
  }

  if (loading) {
    return (
      <div className="fd-profile">
        <div className="fd-profile__header">
          <h1 className="fd-profile__title">Profile</h1>
        </div>
        <div className="fd-profile__panel">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fd-profile">
        <div className="fd-profile__header">
          <h1 className="fd-profile__title">Profile</h1>
        </div>
        <div className="fd-profile__panel fd-profile__error">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const { doctor, clinic, services } = data;

  const bookingHref = toExternalUrl(doctor.firstAppointmentBookingUrl);
  const clinicWebsiteHref = toExternalUrl(clinic.website);

  // ✅ New ClinicAdmin-style hours display (read-only)
  const hasClinicAdminHours =
    !!clinic?.clinicSettings?.hours && typeof clinic?.clinicSettings?.hours === "object";
  const hoursDisplay = getClinicHoursDisplay(clinic);

  // ✅ Legacy list fallback
  const specialHours = hasClinicAdminHours ? [] : normalizeSpecialHoursFromClinicSettings(clinic);

  return (
    <div className="fd-profile">
      <div className="fd-profile__header">
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div>
            <h1 className="fd-profile__title">Profile</h1>
            <p className="fd-profile__subtitle">
              View your details and update your intake settings and services.
            </p>
          </div>

          {!editMode ? (
            <button className="fd-profile__btn" onClick={() => setEditMode(true)}>
              Edit profile
            </button>
          ) : (
            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="fd-profile__btn fd-profile__btn--ghost"
                onClick={onCancelEdits}
              >
                Cancel
              </button>
              <button className="fd-profile__btn" onClick={() => setEditMode(false)}>
                Done
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Doctor identity (always read-only) */}
      <div className="fd-profile__panel">
        <div className="fd-profile__sectionTitle">Doctor identity</div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Full name</div>
          <div className="fd-profile__value">{doctor.fullName || "—"}</div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">CPSO number</div>
          <div className="fd-profile__value">{identityCpso}</div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Gender</div>
          <div className="fd-profile__value">{doctor.gender || "—"}</div>
        </div>
      </div>

      {/* Section A: My Practice Intake */}
      <details className="fd-profile__panel" open>
        <summary className="fd-profile__summary">
          <div>
            <div className="fd-profile__sectionTitle">My Practice Intake</div>
            <div className="fd-profile__hint">Doctor-managed</div>
          </div>
        </summary>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Accepting new patients</div>

          <div
            className="fd-profile__value"
            style={{ display: "flex", gap: 12, alignItems: "center" }}
          >
            {!editMode ? (
              <span>{doctor.acceptingNewPatients ? "Yes" : "No"}</span>
            ) : (
              <label style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                <input
                  type="checkbox"
                  checked={draftAccepting}
                  disabled={savingIntake}
                  onChange={(e) => setDraftAccepting(e.target.checked)}
                />
                <span>{draftAccepting ? "Yes" : "No"}</span>
              </label>
            )}

            <span style={{ color: "#6b7a94", fontSize: 13 }}>Shown to public</span>
          </div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Last confirmed</div>
          <div className="fd-profile__value" style={{ color: "#6b7a94" }}>
            {formatDateTime(doctor.acceptingStatusUpdatedAt)}
          </div>
        </div>


        <div className="fd-profile__row">
          <div className="fd-profile__label">Intake notes</div>
          <div className="fd-profile__value">
            {!editMode ? (
              <div style={{ whiteSpace: "pre-wrap" }}>{doctor.intakeNotes || "—"}</div>
            ) : (
              <>
                <textarea
                  value={draftIntakeNotes}
                  disabled={savingIntake}
                  onChange={(e) => setDraftIntakeNotes(e.target.value)}
                  rows={4}
                  placeholder="Optional. E.g., catchment notes, exclusions, required context…"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid #d1d9e6",
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                />
                <div style={{ marginTop: 6, color: "#6b7a94", fontSize: 13 }}>
                  Not Shown to public - only for internal clinic staff use.
                </div>
              </>
            )}
          </div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Book your first appointment link</div>
          <div className="fd-profile__value">
            {!editMode ? (
              bookingHref ? (
                <a href={bookingHref} target="_blank" rel="noreferrer">
                  {doctor.firstAppointmentBookingUrl}
                </a>
              ) : (
                "—"
              )
            ) : (
              <>
                <input
                  type="url"
                  value={draftBookingUrl}
                  disabled={savingIntake}
                  onChange={(e) => setDraftBookingUrl(e.target.value)}
                  placeholder="Optional. https://medeo.com/..."
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid #d1d9e6",
                    fontFamily: "inherit",
                  }}
                />
                <div style={{ marginTop: 6, color: "#6b7a94", fontSize: 13 }}>
                  Not shown to public (sent in acceptance email if set)
                </div>
              </>
            )}
          </div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Heartbeat rule</div>
          <div className="fd-profile__value" style={{ color: "#6b7a94" }}>
            To stay listed as “Accepting”, please confirm this status at least every 30 days. If it is not confirmed, it may automatically change to “Not accepting.”
          </div>
        </div>

        {intakeError ? (
          <div className="fd-profile__row">
            <div className="fd-profile__label"></div>
            <div className="fd-profile__value" style={{ color: "#b00020" }}>
              {intakeError}
            </div>
          </div>
        ) : null}

        {editMode ? (
          <div className="fd-profile__row">
            <div className="fd-profile__label"></div>
            <div
              className="fd-profile__value"
              style={{ display: "flex", gap: 12, alignItems: "center" }}
            >
              <button
                className="fd-profile__btn"
                onClick={onSaveIntake}
                disabled={savingIntake}
              >
                {savingIntake ? "Saving…" : "Save Intake"}
              </button>

              {intakeSavedAt ? (
                <span style={{ color: "#6b7a94", fontSize: 13 }}>
                  Saved {formatDateTime(intakeSavedAt)}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </details>

      {/* Section B: Capacity */}
      <details className="fd-profile__panel" open>
        <summary className="fd-profile__summary">
          <div>
            <div className="fd-profile__sectionTitle">Capacity</div>
            <div className="fd-profile__hint">Doctor-managed, internal portal only</div>
          </div>
        </summary>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Declared intake capacity</div>
          <div className="fd-profile__value">
            {!editMode ? (
              <span>{doctor.declaredCapacity ?? "—"}</span>
            ) : (
              <>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={draftDeclaredCapacity}
                  disabled={savingCapacity}
                  onChange={(e) => setDraftDeclaredCapacity(e.target.value)}
                  placeholder="Optional. e.g., 50"
                  style={{
                    width: "100%",
                    maxWidth: 260,
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid #d1d9e6",
                    fontFamily: "inherit",
                  }}
                />
                <div style={{ marginTop: 6, color: "#6b7a94", fontSize: 13 }}>
                  Total number of new patients you are prepared to attach in this intake cycle.
                </div>
              </>
            )}
          </div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Remaining capacity override</div>
          <div className="fd-profile__value">
            {!editMode ? (
              <span>{doctor.remainingCapacityOverride ?? "—"}</span>
            ) : (
              <>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={draftRemainingCapacityOverride}
                  disabled={savingCapacity}
                  onChange={(e) => setDraftRemainingCapacityOverride(e.target.value)}
                  placeholder="Optional. Leave blank to calculate automatically"
                  style={{
                    width: "100%",
                    maxWidth: 320,
                    padding: "10px 12px",
                    borderRadius: 12,
                    border: "1px solid #d1d9e6",
                    fontFamily: "inherit",
                  }}
                />
                <div style={{ marginTop: 6, color: "#6b7a94", fontSize: 13 }}>
                  Optional. Use only if calculated remaining capacity does not reflect reality, such as off-platform attachments or clinic-level adjustments.
                </div>
              </>
            )}
          </div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Capacity last updated</div>
          <div className="fd-profile__value" style={{ color: "#6b7a94" }}>
            {formatDateTime(doctor.remainingCapacityUpdatedAt)}
          </div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">How capacity is used</div>
          <div className="fd-profile__value" style={{ color: "#6b7a94" }}>
            Used and remaining capacity are calculated from completed patient attachments. This is internal and is not shown as a promise to patients.
          </div>
        </div>

        {capacityError ? (
          <div className="fd-profile__row">
            <div className="fd-profile__label"></div>
            <div className="fd-profile__value" style={{ color: "#b00020" }}>
              {capacityError}
            </div>
          </div>
        ) : null}

        {editMode ? (
          <div className="fd-profile__row">
            <div className="fd-profile__label"></div>
            <div
              className="fd-profile__value"
              style={{ display: "flex", gap: 12, alignItems: "center" }}
            >
              <button
                className="fd-profile__btn"
                onClick={onSaveCapacity}
                disabled={savingCapacity}
              >
                {savingCapacity ? "Saving…" : "Save Capacity"}
              </button>

              {capacitySavedAt ? (
                <span style={{ color: "#6b7a94", fontSize: 13 }}>
                  Saved {formatDateTime(capacitySavedAt)}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </details>

      <IntakeRulesSection
        editMode={editMode}
        intakeRules={data.intakeRules || []}
        formatDateTime={formatDateTime}
        onSaved={(rules) => {
          setData({
            ...data,
            intakeRules: rules,
          });
        }}
      />

      {/* Section D: My Services */}
      <details className="fd-profile__panel" open>
        <summary className="fd-profile__summary">
          <div>
            <div className="fd-profile__sectionTitle">My Services</div>
            <div className="fd-profile__hint">Doctor-managed</div>
          </div>
        </summary>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Languages</div>
          <div className="fd-profile__value">
            {!editMode ? (
              <div className="fd-profile__chips">
                {(services?.languages || []).length ? (
                  services!.languages.map((x) => (
                    <span key={x.id} className="fd-profile__chip">
                      {x.name}
                    </span>
                  ))
                ) : (
                  <span style={{ color: "#6b7a94" }}>—</span>
                )}
              </div>
            ) : (
              <>
                <select
                  className="fd-profile__multiselect"
                  multiple
                  value={draftLanguageIds.map(String)}
                  disabled={savingServices}
                  onChange={(e) => setDraftLanguageIds(getSelectedIdsFromMultiSelect(e))}
                >
                  {languageOptions.map((opt) => (
                    <option key={opt.id} value={String(opt.id)}>
                      {opt.name}
                    </option>
                  ))}
                </select>
                <div className="fd-profile__help">
                  Tip: hold Ctrl (Windows) / Cmd (Mac) to select multiple.
                </div>
              </>
            )}
          </div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Visit types</div>
          <div className="fd-profile__value">
            {!editMode ? (
              <div className="fd-profile__chips">
                {(services?.visitTypes || []).length ? (
                  services!.visitTypes.map((x) => (
                    <span key={x.id} className="fd-profile__chip">
                      {x.name}
                    </span>
                  ))
                ) : (
                  <span style={{ color: "#6b7a94" }}>—</span>
                )}
              </div>
            ) : (
              <select
                className="fd-profile__multiselect"
                multiple
                value={draftVisitTypeIds.map(String)}
                disabled={savingServices}
                onChange={(e) => setDraftVisitTypeIds(getSelectedIdsFromMultiSelect(e))}
                style={{ minHeight: 110 }}
              >
                {visitTypeOptions.map((opt) => (
                  <option key={opt.id} value={String(opt.id)}>
                    {opt.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Coverage accepted</div>
          <div className="fd-profile__value">
            {!editMode ? (
              <div className="fd-profile__chips">
                {(services?.coverages || []).length ? (
                  services!.coverages.map((x) => (
                    <span key={x.id} className="fd-profile__chip">
                      {x.name}
                    </span>
                  ))
                ) : (
                  <span style={{ color: "#6b7a94" }}>—</span>
                )}
              </div>
            ) : (
              <select
                className="fd-profile__multiselect"
                multiple
                value={draftCoverageTypeIds.map(String)}
                disabled={savingServices}
                onChange={(e) => setDraftCoverageTypeIds(getSelectedIdsFromMultiSelect(e))}
                style={{ minHeight: 110 }}
              >
                {coverageOptions.map((opt) => (
                  <option key={opt.id} value={String(opt.id)}>
                    {opt.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {servicesError ? (
          <div className="fd-profile__row">
            <div className="fd-profile__label"></div>
            <div className="fd-profile__value" style={{ color: "#b00020" }}>
              {servicesError}
            </div>
          </div>
        ) : null}

        {editMode ? (
          <div className="fd-profile__row">
            <div className="fd-profile__label"></div>
            <div
              className="fd-profile__value"
              style={{ display: "flex", gap: 12, alignItems: "center" }}
            >
              <button
                className="fd-profile__btn"
                onClick={onSaveServices}
                disabled={savingServices}
              >
                {savingServices ? "Saving…" : "Save Services"}
              </button>

              {servicesSavedAt ? (
                <span style={{ color: "#6b7a94", fontSize: 13 }}>
                  Saved {formatDateTime(servicesSavedAt)}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </details>

      {/* Section E: Clinic Information (read-only) */}
      <details className="fd-profile__panel" open>
        <summary className="fd-profile__summary">
          <div>
            <div className="fd-profile__sectionTitle">Clinic Information</div>
            <div className="fd-profile__hint">Clinic-managed (read-only)</div>
          </div>
        </summary>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Clinic</div>
          <div className="fd-profile__value">{clinic.name || "—"}</div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Clinic setting</div>
          <div className="fd-profile__value">{clinic.clinicSetting?.name || "—"}</div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Address</div>
          <div className="fd-profile__value">
            {[clinic.addressLine1, clinic.addressLine2, clinic.city, clinic.postalCode]
              .filter(Boolean)
              .join(", ") || "—"}
          </div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Phone</div>
          <div className="fd-profile__value">{clinic.phone || "—"}</div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Fax</div>
          <div className="fd-profile__value">{clinic.fax || "—"}</div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Website</div>
          <div className="fd-profile__value">
            {clinicWebsiteHref ? (
              <a href={clinicWebsiteHref} target="_blank" rel="noreferrer">
                {clinic.website}
              </a>
            ) : (
              "—"
            )}
          </div>
        </div>

        <div className="fd-profile__row">
          <div className="fd-profile__label">Clinic admin email</div>
          <div className="fd-profile__value">{clinic.adminEmail || "—"}</div>
        </div>

        {clinic.operatingHours && clinic.operatingHours.length ? (
          <div className="fd-profile__row">
            <div className="fd-profile__label">Operating hours</div>
            <div className="fd-profile__value">
              <div className="fd-profile__kvlist">
                {clinic.operatingHours.map((h, idx) => (
                  <div key={idx} className="fd-profile__kvrow">
                    <div className="fd-profile__kvkey">{dayName(h.dayOfWeek)}</div>
                    <div className="fd-profile__kvval">
                      {h.isClosed
                        ? "Closed"
                        : `${compactTime(h.openTime)} – ${compactTime(h.closeTime)}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {/* ✅ Clinic Admin-style special hours (read-only) */}
        {hasClinicAdminHours ? (
          <>
            <div className="fd-profile__row">
              <div className="fd-profile__label">Closures &amp; holidays</div>
              <div className="fd-profile__value">
                {hoursDisplay.closures.length ? (
                  <div className="fd-profile__kvlist">
                    {hoursDisplay.closures.map((c, idx) => (
                      <div key={`${c.date}-${idx}`} className="fd-profile__kvrow">
                        <div className="fd-profile__kvkey fd-profile__kvkey--wide">
                          {c.date}
                        </div>
                        <div className="fd-profile__kvval">
                          {c.reason || "Holiday closure"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  "—"
                )}
              </div>
            </div>

            <div className="fd-profile__row">
              <div className="fd-profile__label">Temporary hours override</div>
              <div className="fd-profile__value">
                {hoursDisplay.temporaryOverride ? (
                  <div>
                    <div className="fd-profile__kvlist">
                      <div className="fd-profile__kvrow">
                        <div className="fd-profile__kvkey fd-profile__kvkey--wide">
                          {(hoursDisplay.temporaryOverride.start || "—") +
                            " → " +
                            (hoursDisplay.temporaryOverride.end || "—")}
                        </div>
                        <div className="fd-profile__kvval"></div>
                      </div>
                    </div>

                    {hoursDisplay.temporaryOverride.notePublic ? (
                      <div style={{ marginTop: 6, color: "#6b7a94" }}>
                        {hoursDisplay.temporaryOverride.notePublic}
                      </div>
                    ) : null}

                    <div style={{ marginTop: 6 }}>
                      {renderHoursByDayForDisplay(hoursDisplay.temporaryOverride.hoursByDay) ||
                        "—"}
                    </div>
                  </div>
                ) : (
                  "—"
                )}
              </div>
            </div>

            <div className="fd-profile__row">
              <div className="fd-profile__label">Walk-in hours</div>
              <div className="fd-profile__value">
                {hoursDisplay.walkIn?.enabled ? (
                  <div>
                    {hoursDisplay.walkIn.notePublic ? (
                      <div style={{ marginBottom: 6, color: "#6b7a94" }}>
                        {hoursDisplay.walkIn.notePublic}
                      </div>
                    ) : null}
                    {renderHoursByDayForDisplay(hoursDisplay.walkIn.hoursByDay) || "—"}
                  </div>
                ) : (
                  "—"
                )}
              </div>
            </div>
          </>
        ) : specialHours && specialHours.length ? (
          <div className="fd-profile__row">
            <div className="fd-profile__label">Special hours</div>
            <div className="fd-profile__value">
              <div className="fd-profile__kvlist">
                {specialHours.map((x, idx) => renderSpecialHourRow(x, idx))}
              </div>
              <div style={{ marginTop: 6, color: "#6b7a94", fontSize: 13 }}>
                Managed by the clinic (holiday closures, modified hours, etc.).
              </div>
            </div>
          </div>
        ) : null}

        {clinic.accessibilityFeatures && clinic.accessibilityFeatures.length ? (
          <div className="fd-profile__row">
            <div className="fd-profile__label">Accessibility</div>
            <div className="fd-profile__value">
              <div className="fd-profile__chips">
                {clinic.accessibilityFeatures.map((x) => (
                  <span key={x.id} className="fd-profile__chip">
                    {x.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {clinic.onSiteServices && clinic.onSiteServices.length ? (
          <div className="fd-profile__row">
            <div className="fd-profile__label">On-site services</div>
            <div className="fd-profile__value">
              <div className="fd-profile__chips">
                {clinic.onSiteServices.map((x) => (
                  <span key={x.id} className="fd-profile__chip">
                    {x.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        <div className="fd-profile__row">
          <div className="fd-profile__label"></div>
          <div className="fd-profile__value" style={{ color: "#6b7a94" }}>
            Need to correct clinic information? Contact your clinic admin.
          </div>
        </div>
      </details>
    </div>
  );
}